import React, { useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogActions,
  Typography, Switch, Box, Button, IconButton, alpha, useTheme,
  Divider, Stack
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandshake, faPeopleRoof, faPeopleGroup, faPlus,
  faUser, faLock, faCalendar, faFileAlt, faSignature
} from "@fortawesome/free-solid-svg-icons";
import { Close as CloseIcon } from "@mui/icons-material";

import { CustomDateTime, CustomInput } from "../../../../../CustomForm";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import AddPositionDialog from "./AddPositionDialog";
import { useSnackbar } from "notistack";
import { createCommunicationNote } from "../../../../../../api/services/Communication/communicationNote";
import { fetchPositions } from "../../../../../../api/services/Communication/position";
import { QK } from "../../../../../../base/qk";
import { useParams } from "react-router-dom";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import SvgSignatureField from "../../../../../SignaturePad/SvgSignatureField";
import { useMutation, useQuery, useQueryClient } from "react-query";

const shareOptions = [
  { name: "handover", icon: faHandshake, label: "Handover" },
  { name: "manager", icon: faPeopleRoof, label: "Manager" },
  { name: "family", icon: faPeopleGroup, label: "Family" },
];

const CommunicationFormDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const { clientID } = useParams();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  
  const [addPosOpen, setAddPosOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", reason: "", outcome: "", signature: null,
    datetime: "", private_note: false, handover: false,
    manager: false, family: false, selectedPosition: "", client: "",
  });

  const handleSignatureChange = (dataUrl) => {
    setForm((prev) => ({ ...prev, signature: dataUrl }));
  };

  const { data: fetchedPositions = {} } = useQuery({
    queryKey: [QK.positions],
    queryFn: fetchPositions
  });

  const positions = useMemo(() => (
    fetchedPositions?.results?.map((p) => ({ label: p.name, value: p.id })) || []
  ), [fetchedPositions]);

  const mutation = useMutation({
    mutationFn: createCommunicationNote,
    onSuccess: () => {
      enqueueSnackbar("Note created successfully", { variant: "success" });
      onClose();
      queryClient.invalidateQueries({ queryKey: [QK.commuicationNotes] });
    },
    onError: () => enqueueSnackbar("Failed to create note", { variant: "error" }),
  });

  const handleSave = () => {
    mutation.mutate({ ...form, client: clientID, position: form.selectedPosition, created_by: 0 });
  };

  const toggleShareOption = (key) => setForm((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              overflow: 'hidden',
              m: 2,
              width: 'calc(100% - 32px)',
              maxWidth: '900px'
            }
          }
        }}
      >
        <Box sx={{ 
          px: 3, 
          py: 2, 
          bgcolor: '#ffffff',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <Typography variant="h6" fontWeight={600} color="#1A1A2E">
            New Communication Note
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: '#666' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '280px' }}>
              <Stack spacing={3}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <FontAwesomeIcon icon={faUser} size="sm" color={theme.palette.primary.main} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary">STAFF INFORMATION</Typography>
                  </Box>
                  <CustomInput
                    placeholder="Staff name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    fullWidth
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <FontAwesomeIcon icon={faFileAlt} size="sm" color={theme.palette.primary.main} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary">REASON / SUBJECT</Typography>
                  </Box>
                  <CustomInput
                    placeholder="What is this about?"
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    multiline
                    rows={2}
                    fullWidth
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <FontAwesomeIcon icon={faUser} size="sm" color={theme.palette.primary.main} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary">POSITION</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <CustomSelect
                        value={form.selectedPosition}
                        options={positions}
                        onChange={(e) => setForm({ ...form, selectedPosition: e.target.value })}
                        placeholder="Select position"
                      />
                    </Box>
                    <Button
                      onClick={() => setAddPosOpen(true)}
                      variant="outlined"
                      sx={{ minWidth: 40, height: 40, p: 0 }}
                    >
                      <FontAwesomeIcon icon={faPlus} size="sm" />
                    </Button>
                  </Box>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <FontAwesomeIcon icon={faCalendar} size="sm" color={theme.palette.primary.main} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary">DATE & TIME</Typography>
                  </Box>
                  <CustomDateTime
                    value={form.datetime}
                    onChange={(e) => setForm({ ...form, datetime: e.target.value })}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <FontAwesomeIcon icon={faLock} size="sm" color={theme.palette.error.main} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary">PRIVACY</Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    borderRadius: 2,
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    bgcolor: '#fafafa'
                  }}>
                    <Typography variant="body2" fontWeight={500}>Private Note</Typography>
                    <Switch
                      checked={form.private_note}
                      onChange={(e) => setForm({ ...form, private_note: e.target.checked })}
                      size="small"
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ flex: '1 1 45%', minWidth: '280px' }}>
              <Stack spacing={3}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <FontAwesomeIcon icon={faFileAlt} size="sm" color={theme.palette.primary.main} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary">OUTCOME</Typography>
                  </Box>
                  <CustomTextArea
                    placeholder="Document the conversation results..."
                    rows={4}
                    value={form.outcome}
                    onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <FontAwesomeIcon icon={faSignature} size="sm" color={theme.palette.primary.main} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary">SIGNATURE</Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    borderRadius: 2,
                    bgcolor: '#fafafa'
                  }}>
                    <SvgSignatureField
                      label="Signature"
                      value={form.signature || ""}
                      onChange={handleSignatureChange}
                      height={100}
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ flex: '1 1 100%' }}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  SHARE WITH
                </Typography>
              </Divider>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 1 }}>
                {shareOptions.map((opt) => (
                  <Button
                    key={opt.name}
                    onClick={() => toggleShareOption(opt.name)}
                    variant={form[opt.name] ? "contained" : "outlined"}
                    startIcon={<FontAwesomeIcon icon={opt.icon} />}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 3,
                      py: 1,
                      minWidth: 110,
                      ...(form[opt.name] && {
                        bgcolor: theme.palette.primary.main,
                        '&:hover': { bgcolor: theme.palette.primary.dark }
                      })
                    }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          px: 3, 
          py: 2, 
          bgcolor: '#fafafa', 
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, 
          gap: 2 
        }}>
          <Button onClick={onClose} sx={{ color: '#666', fontWeight: 500, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={mutation.isLoading}
            sx={{ 
              px: 4, 
              fontWeight: 600, 
              textTransform: 'none',
              bgcolor: '#0f172a',
              '&:hover': { bgcolor: '#1e293b' }
            }}
          >
            {mutation.isLoading ? "Saving..." : "Create Note"}
          </Button>
        </DialogActions>
      </Dialog>

      <AddPositionDialog
        open={addPosOpen}
        onClose={() => setAddPosOpen(false)}
        onAdd={() => queryClient.invalidateQueries({ queryKey: [QK.positions] })}
      />
    </>
  );
};

export default CommunicationFormDialog;